/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import _ from 'lodash';
import { View } from 'backbone.marionette';
import Backbone from 'backbone';

import lazyLoadView from '@/util/lazyLoadView';
import showErrorMessage from '@/util/showErrorMessage';

import loadSearchFields from '../../../login/loaders/SearchFieldsViewLoader';
import FindInternalUserByNameOrEmail from '../../services/FindInternaluserByNameOrEmail';
import UserSearchGridView from '../UserSearchGridView/UserSearchGridView';
import FindExternalUserByNameOrEmailOrCommandName from '../../../administration/services/FindExternalUserByNameOrEmailOrCommandName';
import FindExternalUserByCommandIdAndNameOrEmail from '../../../administration/services/FindExternalUserByCommandIdAndNameOrEmail';

import tmplUserSearchModalView from './UserSearchModalView.hbs';

const UserSearchModalView = View.extend({
  template: tmplUserSearchModalView,

  className: 'user-search-modal-view',

  ui: {
    searchFields: '.search-fields',
    userGrid: '.user-grid',
    btnSelect: '.btn-select'
  },

  regions: {
    searchFields: '@ui.searchFields',
    userGrid: '@ui.userGrid'
  },

  childViewEvents: {
    'search:response': 'onSearchResponse'
  },

  events: {
    'click @ui.btnSelect': 'onClickBtnSelect'
  },

  onRender() {
    this.showSearchFieldsView();
    this.showUsersGridView();
  },

  showSearchFieldsView() {
    let searchService;
    let passedParameter;
    if (this.model.get('searchType') === 'ExternalUserSearchByCommandId') {
      searchService = FindExternalUserByCommandIdAndNameOrEmail;
      passedParameter = {
        commandId: this.model.get('commandId')
      };
    } else if (this.model.get('searchType') === 'External') {
      searchService = FindExternalUserByNameOrEmailOrCommandName;
    } else {
      searchService = FindInternalUserByNameOrEmail;
    }

    const searchFields = [
      {
        id: 'firstNameSearchField',
        name: 'firstName',
        class: 'InputSearchField',
        placeholder: 'First Name'
      },
      {
        id: 'lastNameSearchField',
        name: 'lastName',
        class: 'InputSearchField',
        placeholder: 'Last Name'
      },
      {
        id: 'emailSearchFiled',
        name: 'email',
        class: 'InputSearchField',
        placeholder: 'Email'
      }
    ];
    if (this.model.get('searchType') === 'ExternalUserSearchByCommandId') {
      this.showChildView(
        'searchFields',
        lazyLoadView(() => loadSearchFields(searchFields, searchService, passedParameter))
      );
    } else {
      this.showChildView(
        'searchFields',
        lazyLoadView(() => loadSearchFields(searchFields, searchService))
      );
    }
  },

  showUsersGridView() {
    this.userGridView = new UserSearchGridView({
      model: new Backbone.Model({})
    });
    this.showChildView('userGrid', this.userGridView);
  },

  onSearchResponse(response) {
    let users;
    if (response.users) {
      users = _.map(response.users, (user) => ({ ...user, name: `${user.firstName} ${user.lastName}` }));
    } else {
      users = _.map(response, (user) => ({ ...user, name: `${user.firstName} ${user.lastName}` }));
    }
    this.userGridView.collection.fullCollection.reset(users);
  },

  async onClickBtnSelect(e) {
    e.preventDefault();

    if (this.userGridView.viewGrid.getSelectedModels().length < 1) {
      this.showNoUserSelectionError();
    }

    if (this.userGridView.viewGrid.getSelectedModels().length > 1) {
      this.showMultipleUserSelectionError();
    }

    const selectedUser = this.userGridView.viewGrid.getSelectedModels()[0].toJSON();
    this.triggerMethod('modal:close', selectedUser);
  },

  showNoUserSelectionError() {
    showErrorMessage({
      errorCode: 'noUserSelectionError',
      errorTitle: 'Selection Error',
      errorMessage: 'A User must be selected.'
    });
  },

  showMultipleUserSelectionError() {
    showErrorMessage({
      errorCode: 'multipleUserSelectionError',
      errorTitle: 'Selection Error',
      errorMessage: 'Only a single User may be selected.'
    });
  },

  showUserSearchError() {
    showErrorMessage({
      errorCode: 'searchError',
      errorTitle: 'Search Error',
      errorMessage:
        'An error occurred while searching Users. Try again or contact an administrator if your issue persists.'
    });
  }
});

export default UserSearchModalView;
